/** @format */

import {ComponentType} from 'react';

import CampaignIcon from '@mui/icons-material/Campaign';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import FolderIcon from '@mui/icons-material/Folder';
import KeyIcon from '@mui/icons-material/Key';
import KeyboardCommandKeyIcon from '@mui/icons-material/KeyboardCommandKey';
import PublicIcon from '@mui/icons-material/Public';
import DevicesIcon from '@mui/icons-material/Devices';

import TextAdIcon from './icons/text-ad.svg?react';
import ViewColumnsIcon from './icons/view-columns.svg?react';

export type EntityType =
  | 'device'
  | 'os'
  | 'segment'
  | 'location'
  | 'placement'
  | 'ad'
  | 'campaign'
  | 'group'
  | 'keyword';

export type ActionType =
  | 'LOWER_BID'
  | 'INCREASE_BID'
  | 'CHECK'
  | 'MONITOR'
  | 'DISABLE'
  | 'ENABLE'
  | 'LOWER_BUDGET'
  | 'INCREASE_BUDGET';

// Маппинг типов действий на русские названия
export const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  LOWER_BID: 'Понизить ставку на',
  INCREASE_BID: 'Повысить ставку на',
  DISABLE: 'Отключить',
  ENABLE: 'Включить',
  CHECK: 'Провести доп. анализ т.к. очень много конверсий',
  MONITOR: 'Ждать накопления статистики',
  INCREASE_BUDGET: 'Увеличить бюджет на',
  LOWER_BUDGET: 'Уменьшить бюджет на',
};

// Маппинг типов сущностей на заголовки сегментов
export const ENTITY_TYPE_HEADERS: Record<EntityType, string> = {
  device: 'Тип устройства',
  os: 'Операционные системы',
  segment: 'Пол и возраст',
  location: 'География',
  placement: 'Площадки',
  ad: 'Объявления',
  campaign: 'Рекламные кампании',
  group: 'Группы объявлений',
  keyword: 'Ключевые слова',
};

// Маппинг типов сущностей на иконки
export const ENTITY_TYPE_ICONS: Record<EntityType, ComponentType<any>> = {
  device: DevicesIcon,
  os: KeyboardCommandKeyIcon,
  segment: FamilyRestroomIcon,
  location: PublicIcon,
  placement: ViewColumnsIcon,
  ad: TextAdIcon,
  campaign: CampaignIcon,
  group: FolderIcon,
  keyword: KeyIcon,
};

export const MODEL_ATTRIBUTION_MAP: Record<string, string> = {
  automatic: 'Автоматическая',
  first: 'Первый переход',
  last: 'Последний переход',
  lastsign: 'Последний значимый переход',
  last_yandex_direct_click: 'Последний клик из Яндекс.Директа',
  cross_device_first: 'Первое взаимодействие на всех устройствах',
  cross_device_last: 'Последнее взаимодействие на всех устройствах',
  cross_device_last_significant: 'Последнее значимое взаимодействие на всех устройствах',
  cross_device_last_yandex_direct_click: 'Последний клик из Яндекс.Директа на всех устройствах',
};

export const DEVICE_TYPE_MAP: Record<string, string> = {
  DESKTOP: 'ПК',
  MOBILE: 'Смартфоны',
  TABLET: 'Планшеты',
  TV: 'ТВ',
};

export const OS_TYPE_MAP: Record<string, string> = {
  ANDROID: 'Android',
  IOS: 'iOS',
  WINDOWS: 'Windows',
  MAC: 'Mac OS',
  LINUX: 'GNU/Linux',
  CHROME: 'Google Chrome OS',
};

export const MOCK_REPORT_DATA = {
  currentUser: {
    company: {
      campaignMediaplanStats: [
        {
          campaignId: 'campaign-1',
          campaignName: 'Campaign 1',
          accountName: 'Account 1',
        },
        {
          campaignId: 'campaign-2',
          campaignName: 'Campaign 2',
          accountName: 'Account 2',
        },
      ],
      marvinFullAiReport: {
        marvinOrderId: 'order-123',
        campaignIds: ['campaign-1', 'campaign-2'],
        goals: [
          {id: 1, name: 'Goal 1', counterId: 100, counterName: 'Counter 1'},
          {id: 2, name: 'Goal 2', counterId: 101, counterName: 'Counter 2'},
        ],
        reportJson: {
          projectInfo: {
            atribModel: 'lastsign',
            buildDate: '2025-12-09',
            clientName: 'Test Client',
            dateFrom: '2025-11-01',
            dateTo: '2025-11-30',
            name: 'Test Report',
            currentMetrics: {
              actualBudget: 50000,
              actualKpi: 250,
              avgCpl: 200,
            },
            goals: {
              kpiName: 'Conversions',
              kpiUnits: 'pcs',
              targetBudget: 100000,
              targetKpi: 500,
            },
          },
          summary: {
            expertResume: 'Test summary information about the report',
            expectedBudget: 95000,
            expectedKpi: 475,
          },
          recommendationGroups: [
            {
              entityType: 'device',
              totalBudgetImpact: 1000,
              totalKpiImpact: 10,
              recommendations: [
                {
                  actionType: 'INCREASE_BID',
                  actionUnits: '%',
                  actionValue: 20,
                  campaignId: 'campaign-1',
                  campaignName: 'Campaign 1',
                  checkCampaigns: false,
                  description: 'Increase bid for mobile devices',
                  device: 'mobile',
                  disabled: false,
                  entityId: 'mobile-1',
                  entityType: 'device',
                  essence: 'Mobile devices show better performance',
                  hypothesisDigital: 'Digital hypothesis',
                  hypothesisTheory: 'Theory hypothesis',
                  id: 'rec-1',
                  location: null,
                  netType: null,
                  notes: 'Additional notes',
                  os: null,
                  placement: null,
                  priority: 1,
                  segmentAge: null,
                  segmentGender: null,
                  tunable: true,
                  forecast: {
                    comment: 'Forecast comment',
                    impactBudgetValue: 500,
                    impactKpiUnits: 'pcs',
                    impactKpiValue: 5,
                  },
                  justification: {
                    budget: 10000,
                    comment: 'Justification comment',
                    conversions: 50,
                    conversionsShare: 20,
                    ctr: 2.5,
                    kpi: 200,
                    kpiDelta: 10,
                  },
                },
              ],
            },
            {
              entityType: 'location',
              totalBudgetImpact: 2000,
              totalKpiImpact: 15,
              recommendations: [
                {
                  actionType: 'LOWER_BID',
                  actionUnits: '%',
                  actionValue: 15,
                  campaignId: 'campaign-2',
                  campaignName: 'Campaign 2',
                  checkCampaigns: false,
                  description: 'Lower bid for region',
                  device: null,
                  disabled: false,
                  entityId: 'region-1',
                  entityType: 'location',
                  essence: 'Region shows poor performance',
                  hypothesisDigital: 'Digital hypothesis 2',
                  hypothesisTheory: 'Theory hypothesis 2',
                  id: 'rec-2',
                  location: 'Moscow',
                  netType: null,
                  notes: 'Additional notes 2',
                  os: null,
                  placement: null,
                  priority: 2,
                  segmentAge: null,
                  segmentGender: null,
                  tunable: true,
                  forecast: {
                    comment: 'Forecast comment 2',
                    impactBudgetValue: 1000,
                    impactKpiUnits: 'pcs',
                    impactKpiValue: 10,
                  },
                  justification: {
                    budget: 20000,
                    comment: 'Justification comment 2',
                    conversions: 100,
                    conversionsShare: 30,
                    ctr: 1.5,
                    kpi: 300,
                    kpiDelta: -5,
                  },
                },
              ],
            },
          ],
        },
      },
    },
  },
};
